import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { isAuthenticated, setAuth, clearAuth, getUserId } from '../auth';
import { API_BASE } from '../config';
import { api, registerSessionExpiredHandler } from '../api';
import { registerFcmToken } from '../services/webPush';
import { identifyUser, trackEvent } from '../services/analytics';

interface AuthContextType {
  isLoggedIn: boolean | null;
  isGuest: boolean;
  isBanned: boolean;
  login: (token: string, userId: string) => Promise<void>;
  logout: () => Promise<void>;
  enterGuest: () => void;
  exitGuest: () => void;
  handleBanned: () => void;
  dismissBanned: () => void;
}

const AuthContext = createContext<AuthContextType>({
  isLoggedIn: null,
  isGuest: false,
  isBanned: false,
  login: async () => {},
  logout: async () => {},
  enterGuest: () => {},
  exitGuest: () => {},
  handleBanned: () => {},
  dismissBanned: () => {},
});

const GUEST_KEY = 'guest_mode';

const BAN_POLL_INTERVAL = 30000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  // Guest = "browse without an account". Kept in sessionStorage so a refresh
  // while previewing doesn't bounce the visitor back to the login wall.
  const [isGuest, setIsGuest] = useState<boolean>(() => {
    try { return sessionStorage.getItem(GUEST_KEY) === '1'; } catch { return false; }
  });
  const [isBanned, setIsBanned] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const enterGuest = useCallback(() => {
    try { sessionStorage.setItem(GUEST_KEY, '1'); } catch { /* ignore */ }
    setIsGuest(true);
  }, []);
  const exitGuest = useCallback(() => {
    try { sessionStorage.removeItem(GUEST_KEY); } catch { /* ignore */ }
    setIsGuest(false);
  }, []);

  const handleBanned = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    clearAuth().catch(() => null);
    setIsLoggedIn(false);
    setIsBanned(true);
  }, []);

  const dismissBanned = useCallback(() => {
    setIsBanned(false);
  }, []);

  const checkBanStatus = useCallback(async () => {
    try {
      await api.get('/auth/me');
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? '';
      if (err?.response?.status === 401 && typeof msg === 'string' && msg.toLowerCase().includes('banned')) {
        handleBanned();
      }
    }
  }, [handleBanned]);

  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(checkBanStatus, BAN_POLL_INTERVAL);
  }, [checkBanStatus]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const logout = useCallback(async () => {
    stopPolling();
    // H1 phase 2 — refresh token is in the httpOnly cookie. POST with
    // withCredentials so the browser attaches it; the server reads it
    // and revokes server-side, then clears the cookie via Set-Cookie.
    // Best-effort — even if the network call fails we still clear local
    // state below so the UI lands on /login.
    try {
      await axios.post(
        `${API_BASE}/auth/logout`,
        {},
        { timeout: 5000, withCredentials: true },
      ).catch(() => null);
    } catch { /* ignore */ }
    try { await clearAuth(); } catch { /* ignore */ }
    identifyUser(null);
    trackEvent('logout');
    setIsBanned(false);
    setIsLoggedIn(false);
  }, [stopPolling]);

  useEffect(() => {
    registerSessionExpiredHandler(() => { setIsLoggedIn(false); });
    isAuthenticated().then(authenticated => {
      setIsLoggedIn(authenticated);
      if (authenticated) {
        startPolling();
        registerFcmToken();
        getUserId().then(uid => { if (uid) identifyUser(uid); });
      }
    });
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  const login = async (token: string, userId: string) => {
    await setAuth(token, userId);
    identifyUser(userId);
    trackEvent('login');
    exitGuest();
    setIsBanned(false);
    setIsLoggedIn(true);
    startPolling();
    registerFcmToken();
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn, isGuest, isBanned, login, logout, enterGuest, exitGuest, handleBanned, dismissBanned }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
